import { useEffect } from "react";
import { ShieldCheck, Award, Clock, HeartHandshake } from "lucide-react";
import { IMAGES } from "@/data/site";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { BrandsMarquee } from "@/components/BrandsMarquee";

const values = [
  { icon: Award, t: "Specialists, Not Generalists", d: "Water heaters are our core trade. Our techs train on them, stock parts for them, and work on them daily — that focus shows in the result." },
  { icon: ShieldCheck, t: "Licensed, Insured, Permitted", d: "CA C-36 licensed and fully insured. Every install pulled through the City of Irvine permit process and inspected — no shortcuts." },
  { icon: Clock, t: "Built Around Emergencies", d: "Hot water failures don't schedule themselves. Live 24/7 answer and same-day service are how this company was designed from day one." },
  { icon: HeartHandshake, t: "Neighbors First", d: "We live here. Woodbridge, Turtle Rock, Northwood — these are our own neighborhoods, and our reputation rides on every job." },
];

export default function About() {
  useEffect(() => {
    document.title = "About Us | Irvine Water Heater Man";
  }, []);

  return (
    <div>
      <section className="navy-grain text-white px-4 sm:px-6 pt-14 sm:pt-20 pb-16" data-testid="about-hero">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
            <MaskedLine>Irvine's Water Heater</MaskedLine>
            <MaskedLine delay={0.15}><span className="text-[#FF6500]">Specialists.</span></MaskedLine>
          </h1>
          <MaskedLine delay={0.3}>
            <p className="text-white/70 text-lg leading-relaxed max-w-2xl mt-6">
              A local, family-run plumbing company built around one belief: a homeowner without hot water deserves a specialist at the door the same day — at a fair, upfront price.
            </p>
          </MaskedLine>
        </div>
      </section>

      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white" data-testid="about-story">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <img src={IMAGES.technician} alt="Irvine Water Heater Man technician" className="rounded-2xl w-full h-[440px] object-cover" />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[#FF6500] font-heading font-bold uppercase tracking-widest text-sm mb-3">Our Story</p>
            <h2 className="font-heading font-extrabold text-4xl tracking-tight text-gray-900 mb-6">Why We Went All-In On Water Heaters.</h2>
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p>
                Most plumbing companies treat water heaters as one line item among fifty. We saw what that produces: undersized units, skipped permits, botched venting, and homeowners paying twice to get it done right.
              </p>
              <p>
                So we specialized. Our technicians are factory-trained on Bradford White, Rheem, AO Smith, Navien, Rinnai, Noritz, and Takagi. Our trucks are stocked specifically for water heater repair and same-day replacement. Our flat-rate pricing book means the quote you approve is the bill you pay.
              </p>
              <p>
                Fifteen-plus years and thousands of Orange County water heaters later, that focus is still the whole business model: get it right the first time, or come back and make it right — free.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-[#FAFAFA]" data-testid="about-values">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="font-heading font-extrabold text-4xl tracking-tight text-gray-900 mb-12">What We Stand On.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-6">
            {values.map((v, i) => (
              <Reveal key={v.t} delay={i * 0.07}>
                <div className="bg-white border border-gray-200 rounded-2xl p-8 h-full">
                  <v.icon className="w-8 h-8 text-[#FF6500] mb-4" />
                  <h3 className="font-heading font-bold text-xl text-gray-900 mb-2">{v.t}</h3>
                  <p className="text-gray-600 leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <BrandsMarquee />
      <CTABand idPrefix="about-cta" />
    </div>
  );
}
